/**
 * useMyWaitlist — TanStack Query hook backing the Me → Waitlist sub-page.
 *
 * Endpoint: GET /api/v1/me/waitlist
 * Response envelope: { items: WaitlistItem[], nextCursor: null }
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';

export interface WaitlistItem {
  id: string;
  skuCode: string;
  skuName: string;
  notifyAt: string;
  createdAt: string;
}

interface Envelope {
  items: WaitlistItem[];
  nextCursor: string | null;
}

export function useMyWaitlist(): {
  data: WaitlistItem[] | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const jwt = useAuth((s) => s.jwt);

  const query = useQuery<WaitlistItem[], Error>({
    queryKey: ['me-waitlist'],
    queryFn: async () => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await apiFetch('/api/v1/me/waitlist', jwt);
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`me_waitlist_failed: ${res.status} ${body}`);
      }
      const env = (await res.json()) as Envelope;
      return env.items;
    },
    enabled: !!jwt,
    staleTime: 30_000,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
